/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Linking,
  Share,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
  Feather,
  AntDesign,
  Entypo,
  FontAwesome,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as Calendar from 'expo-calendar';
import * as Notifications from 'expo-notifications';

const { width, height } = Dimensions.get('window');

const PracticeExams = () => {
  const navigation = useNavigation();
  const [darkMode, setDarkMode] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [bookmarkedExams, setBookmarkedExams] = useState([]);
  const [completedExams, setCompletedExams] = useState([]);
  const [examResults, setExamResults] = useState({});
  
  // Modal states
  const [examModalVisible, setExamModalVisible] = useState(false);
  const [tipsModalVisible, setTipsModalVisible] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  
  // Exam taking states
  const [takingExam, setTakingExam] = useState(false);
  const [currentExam, setCurrentExam] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);
  
  // Animation values
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  
  const [exams, setExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    averageScore: 0,
    totalTimeSpent: 0,
  });

  const categories = [
    { id: 'all', label: 'All Exams', icon: 'view-grid' },
    { id: 'math', label: 'Mathematics', icon: 'calculator' },
    { id: 'physics', label: 'Physics', icon: 'atom' },
    { id: 'chemistry', label: 'Chemistry', icon: 'flask' },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog' },
    { id: 'electrical', label: 'Electrical', icon: 'flash' },
    { id: 'civil', label: 'Civil', icon: 'bridge' },
    { id: 'computer', label: 'Computer', icon: 'laptop' },
    { id: 'fundamentals', label: 'Fundamentals', icon: 'book-open' },
    { id: 'fe', label: 'FE Exam', icon: 'certificate' },
    { id: 'pe', label: 'PE Exam', icon: 'school' },
  ];

  const filters = [
    { id: 'all', label: 'All', icon: 'view-grid' },
    { id: 'easy', label: 'Easy', icon: 'trending-up' },
    { id: 'medium', label: 'Medium', icon: 'trending-neutral' },
    { id: 'hard', label: 'Hard', icon: 'trending-down' },
    { id: 'timed', label: 'Timed', icon: 'timer' },
    { id: 'untimed', label: 'Untimed', icon: 'timer-off' },
    { id: 'free', label: 'Free', icon: 'currency-usd-off' },
    { id: 'recent', label: 'Recent', icon: 'history' },
  ];

  const gradients = darkMode ? {
    primary: ['#2d3748', '#4a5568'],
    secondary: ['#805ad5', '#d53f8c'],
    success: ['#38a169', '#68d391'],
    warning: ['#d69e2e', '#ecc94b'],
    danger: ['#e53e3e', '#fc8181'],
    info: ['#3182ce', '#63b3ed'],
    purple: ['#805ad5', '#b794f4'],
    orange: ['#dd6b20', '#f6ad55'],
    blue: ['#2b6cb0', '#4299e1'],
    green: ['#38a169', '#68d391'],
    teal: ['#319795', '#81e6d9'],
    pink: ['#d53f8c', '#fbb6ce'],
    indigo: ['#4c51bf', '#6366f1'],
    math: ['#4c51bf', '#6366f1'],
    physics: ['#dd6b20', '#f6ad55'],
    chemistry: ['#d53f8c', '#fbb6ce'],
    mechanical: ['#38a169', '#68d391'],
    electrical: ['#d69e2e', '#f6e05e'],
    civil: ['#3182ce', '#63b3ed'],
    computer: ['#805ad5', '#b794f4'],
  } : {
    primary: ['#6366f1', '#8b5cf6'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    teal: ['#6366f1', '#8b5cf6'],
    pink: ['#ff9a9e', '#fecfef'],
    indigo: ['#6366f1', '#8b5cf6'],
    math: ['#6366f1', '#8b5cf6'],
    physics: ['#fdbb2d', '#22c1c3'],
    chemistry: ['#ff9a9e', '#fecfef'],
    mechanical: ['#56ab2f', '#a8e6cf'],
    electrical: ['#fdbb2d', '#22c1c3'],
    civil: ['#2196f3', '#21cbf3'],
    computer: ['#d299c2', '#fef9d7'],
  };

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
    startAnimations();
    
    return () => {
      const unsubscribe = NetInfo.addEventListener(state => {});
      if (unsubscribe) unsubscribe();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    filterExams();
  }, [searchQuery, selectedFilter, selectedCategory, exams]);

  useFocusEffect(
    React.useCallback(() => {
      refreshData();
      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }, [])
  );

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadExams(),
        loadBookmarks(),
        loadCompletedExams(),
        loadExamResults(),
        loadDarkModePreference(),
      ]);
      calculateStats();
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load practice exams. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await loadExams();
      calculateStats();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadExams = async () => {
    try {
      const mockExams = [
        {
          id: 'E001',
          title: 'FE Electrical and Computer Exam',
          category: 'fe',
          subcategory: 'electrical',
          difficulty: 'hard',
          duration: 240, // minutes
          questions: 110,
          passingScore: 70,
          isTimed: true,
          isFree: true,
          price: 0,
          description: 'Fundamentals of Engineering (FE) Electrical and Computer practice exam. Covers all topics from the NCEES FE Reference Handbook.',
          topics: [
            'Mathematics',
            'Probability and Statistics',
            'Ethics and Professional Practice',
            'Engineering Economics',
            'Properties of Electrical Materials',
            'Circuit Analysis',
            'Linear Systems',
            'Signal Processing',
            'Electronics',
            'Power Systems',
            'Electromagnetics',
            'Control Systems',
            'Communications',
            'Computer Networks',
            'Digital Systems',
            'Computer Systems',
            'Software Development'
          ],
          color: '#FF6B35',
          icon: 'flash',
          createdDate: '2024-01-15',
          lastUpdated: '2024-03-01',
          attempts: 12540,
          averageScore: 68.5,
          questionsData: generateFEQuestions(),
        },
        {
          id: 'E002',
          title: 'Engineering Mathematics Quiz',
          category: 'math',
          subcategory: 'calculus',
          difficulty: 'medium',
          duration: 60,
          questions: 30,
          passingScore: 60,
          isTimed: true,
          isFree: true,
          price: 0,
          description: 'Test your engineering mathematics skills with this comprehensive quiz covering calculus, linear algebra, and differential equations.',
          topics: [
            'Calculus',
            'Linear Algebra',
            'Differential Equations',
            'Complex Analysis',
            'Probability',
            'Statistics',
            'Numerical Methods'
          ],
          color: '#4c51bf',
          icon: 'calculator',
          createdDate: '2024-02-10',
          lastUpdated: '2024-03-15',
          attempts: 8920,
          averageScore: 72.3,
          questionsData: generateMathQuestions(),
        },
        {
          id: 'E003',
          title: 'Mechanical Engineering Fundamentals',
          category: 'mechanical',
          subcategory: 'fundamentals',
          difficulty: 'medium',
          duration: 90,
          questions: 50,
          passingScore: 65,
          isTimed: true,
          isFree: true,
          price: 0,
          description: 'Comprehensive test covering mechanical engineering fundamentals including statics, dynamics, thermodynamics, and materials science.',
          topics: [
            'Statics',
            'Dynamics',
            'Mechanics of Materials',
            'Thermodynamics',
            'Fluid Mechanics',
            'Heat Transfer',
            'Materials Science',
            'Manufacturing Processes'
          ],
          color: '#38a169',
          icon: 'cog',
          createdDate: '2024-01-20',
          lastUpdated: '2024-03-10',
          attempts: 7450,
          averageScore: 65.8,
          questionsData: generateMechanicalQuestions(),
        },
        {
          id: 'E004',
          title: 'Civil Engineering Structures',
          category: 'civil',
          subcategory: 'structures',
          difficulty: 'hard',
          duration: 120,
          questions: 60,
          passingScore: 70,
          isTimed: true,
          isFree: false,
          price: 29.99,
          description: 'Advanced structural analysis and design exam for civil engineering students and professionals.',
          topics: [
            'Structural Analysis',
            'Steel Design',
            'Concrete Design',
            'Foundation Engineering',
            'Earthquake Engineering',
            'Bridge Design',
            'Construction Management'
          ],
          color: '#3182ce',
          icon: 'bridge',
          createdDate: '2024-02-15',
          lastUpdated: '2024-03-20',
          attempts: 3210,
          averageScore: 63.2,
          questionsData: generateCivilQuestions(),
        },
        {
          id: 'E005',
          title: 'Computer Science Algorithms',
          category: 'computer',
          subcategory: 'algorithms',
          difficulty: 'medium',
          duration: 45,
          questions: 25,
          passingScore: 75,
          isTimed: true,
          isFree: true,
          price: 0,
          description: 'Test your algorithm knowledge with this computer science practice exam covering data structures and algorithmic thinking.',
          topics: [
            'Data Structures',
            'Algorithms',
            'Time Complexity',
            'Sorting Algorithms',
            'Search Algorithms',
            'Dynamic Programming',
            'Graph Algorithms'
          ],
          color: '#805ad5',
          icon: 'laptop',
          createdDate: '2024-02-28',
          lastUpdated: '2024-03-25',
          attempts: 11230,
          averageScore: 69.5,
          questionsData: generateCSQuestions(),
        },
        {
          id: 'E006',
          title: 'Physics for Engineers',
          category: 'physics',
          subcategory: 'mechanics',
          difficulty: 'easy',
          duration: 45,
          questions: 25,
          passingScore: 60,
          isTimed: false,
          isFree: true,
          price: 0,
          description: 'Fundamental physics concepts for engineering students covering mechanics, electricity, and magnetism.',
          topics: [
            'Classical Mechanics',
            'Electricity',
            'Magnetism',
            'Waves',
            'Optics',
            'Modern Physics',
            'Thermodynamics'
          ],
          color: '#dd6b20',
          icon: 'atom',
          createdDate: '2024-01-05',
          lastUpdated: '2024-03-05',
          attempts: 15670,
          averageScore: 76.8,
          questionsData: generatePhysicsQuestions(),
        },
        {
          id: 'E007',
          title: 'Chemical Engineering Principles',
          category: 'chemistry',
          subcategory: 'chemical',
          difficulty: 'hard',
          duration: 180,
          questions: 80,
          passingScore: 70,
          isTimed: true,
          isFree: false,
          price: 39.99,
          description: 'Comprehensive chemical engineering exam covering process design, thermodynamics, and reaction engineering.',
          topics: [
            'Process Calculations',
            'Thermodynamics',
            'Fluid Mechanics',
            'Heat Transfer',
            'Mass Transfer',
            'Reaction Engineering',
            'Process Control',
            'Plant Design'
          ],
          color: '#d53f8c',
          icon: 'flask',
          createdDate: '2024-02-01',
          lastUpdated: '2024-03-18',
          attempts: 2340,
          averageScore: 61.5,
          questionsData: generateChemicalQuestions(),
        },
        {
          id: 'E008',
          title: 'PE Mechanical Practice Exam',
          category: 'pe',
          subcategory: 'mechanical',
          difficulty: 'very-hard',
          duration: 480,
          questions: 80,
          passingScore: 70,
          isTimed: true,
          isFree: false,
          price: 49.99,
          description: 'Professional Engineering (PE) Mechanical practice exam simulating the actual licensure examination.',
          topics: [
            'Thermal and Fluids Systems',
            'Mechanical Systems and Materials',
            'HVAC and Refrigeration',
            'Machine Design',
            'Stress Analysis',
            'Dynamics and Vibrations',
            'Engineering Economics'
          ],
          color: '#38a169',
          icon: 'certificate',
          createdDate: '2024-01-25',
          lastUpdated: '2024-03-22',
          attempts: 1890,
          averageScore: 59.3,
          questionsData: generatePEQuestions(),
        },
      ];
      
      setExams(mockExams);
      setFilteredExams(mockExams);
    } catch (error) {
      console.error('Error loading exams:', error);
      throw error;
    }
  };

  // Mock question generators (simplified for demo)
  const generateFEQuestions = () => {
    return Array(10).fill().map((_, i) => ({
      id: `fe_q${i + 1}`,
      question: `FE Electrical Question ${i + 1}: What is the relationship between voltage, current, and resistance?`,
      options: [
        'V = I/R',
        'I = V*R',
        'V = I*R',
        'R = V*I'
      ],
      correctAnswer: 2,
      explanation: 'Ohm\'s Law states that voltage (V) equals current (I) times resistance (R).',
      topic: 'Circuit Analysis',
      difficulty: i < 3 ? 'easy' : i < 7 ? 'medium' : 'hard'
    }));
  };

  const generateMathQuestions = () => {
    return Array(10).fill().map((_, i) => ({
      id: `math_q${i + 1}`,
      question: `Math Question ${i + 1}: What is the derivative of x²?`,
      options: [
        'x',
        '2x',
        'x²',
        '2x²'
      ],
      correctAnswer: 1,
      explanation: 'Using the power rule: d/dx(xⁿ) = n*xⁿ⁻¹, so d/dx(x²) = 2x.',
      topic: 'Calculus',
      difficulty: 'easy'
    }));
  };

  const generateMechanicalQuestions = () => {
    return Array(10).fill().map((_, i) => ({
      id: `mech_q${i + 1}`,
      question: `Mechanical Question ${i + 1}: What is stress?`,
      options: [
        'Force per unit area',
        'Change in length',
        'Resistance to deformation',
        'Energy per unit volume'
      ],
      correctAnswer: 0,
      explanation: 'Stress is defined as force per unit area (σ = F/A).',
      topic: 'Mechanics of Materials',
      difficulty: i < 4 ? 'easy' : i < 8 ? 'medium' : 'hard'
    }));
  };

  const generateCivilQuestions = () => {
    return Array(10).fill().map((_, i) => ({
      id: `civil_q${i + 1}`,
      question: `Civil Question ${i + 1}: What does E stand for in structural analysis?`,
      options: [
        'Elasticity',
        'Energy',
        'Young\'s Modulus',
        'Efficiency'
      ],
      correctAnswer: 2,
      explanation: 'E represents Young\'s Modulus, which measures the stiffness of a material.',
      topic: 'Structural Analysis',
      difficulty: i < 3 ? 'easy' : i < 7 ? 'medium' : 'hard'
    }));
  };

  const generateCSQuestions = () => {
    return Array(10).fill().map((_, i) => ({
      id: `cs_q${i + 1}`,
      question: `CS Question ${i + 1}: What is the time complexity of binary search?`,
      options: [
        'O(1)',
        'O(n)',
        'O(log n)',
        'O(n²)'
      ],
      correctAnswer: 2,
      explanation: 'Binary search has O(log n) time complexity as it halves the search space each iteration.',
      topic: 'Algorithms',
      difficulty: i < 5 ? 'easy' : 'medium'
    }));
  };

  const generatePhysicsQuestions = () => {
    return Array(10).fill().map((_, i) => ({
      id: `phy_q${i + 1}`,
      question: `Physics Question ${i + 1}: What is Newton\'s First Law?`,
      options: [
        'F = ma',
        'Action = Reaction',
        'An object at rest stays at rest',
        'Energy is conserved'
      ],
      correctAnswer: 2,
      explanation: 'Newton\'s First Law states that an object at rest stays at rest, and an object in motion stays in motion unless acted upon by a force.',
      topic: 'Classical Mechanics',
      difficulty: 'easy'
    }));
  };

  const generateChemicalQuestions = () => {
    return Array(10).fill().map((_, i) => ({
      id: `chem_q${i + 1}`,
      question: `Chemical Engineering Question ${i + 1}: What is Reynolds number used for?`,
      options: [
        'Measuring viscosity',
        'Predicting flow regime',
        'Calculating heat transfer',
        'Determining reaction rate'
      ],
      correctAnswer: 1,
      explanation: 'Reynolds number predicts whether flow is laminar or turbulent.',
      topic: 'Fluid Mechanics',
      difficulty: i < 4 ? 'medium' : 'hard'
    }));
  };

  const generatePEQuestions = () => {
    return Array(10).fill().map((_, i) => ({
      id: `pe_q${i + 1}`,
      question: `PE Mechanical Question ${i + 1}: What is the Carnot efficiency?`,
      options: [
        '1 - T_cold/T_hot',
        'T_hot/T_cold',
        'W/Q_hot',
        'Q_cold/Q_hot'
      ],
      correctAnswer: 0,
      explanation: 'Carnot efficiency = 1 - T_cold/T_hot, where temperatures are in Kelvin.',
      topic: 'Thermodynamics',
      difficulty: 'hard'
    }));
  };

  const loadBookmarks = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedExams');
      if (bookmarks) {
        setBookmarkedExams(JSON.parse(bookmarks));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  };

  const loadCompletedExams = async () => {
    try {
      const completed = await AsyncStorage.getItem('completedExams');
      if (completed) {
        setCompletedExams(JSON.parse(completed));
      }
    } catch (error) {
      console.error('Error loading completed exams:', error);
    }
  };

  const loadExamResults = async () => {
    try {
      const results = await AsyncStorage.getItem('examResults');
      if (results) {
        setExamResults(JSON.parse(results));
      }
    } catch (error) {
      console.error('Error loading exam results:', error);
    }
  };

  const calculateStats = () => {
    const total = exams.length;
    const completed = completedExams.length;
    const averageScore = completed > 0 
      ? Object.values(examResults).reduce((sum, result) => sum + result.score, 0) / completed 
      : 0;
    
    // Calculate total time spent (simplified - would need actual data)
    const totalTimeSpent = completed * 60; // minutes
    
    setStats({ total, completed, averageScore, totalTimeSpent });
  };

  const filterExams = () => {
    let filtered = exams;
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(exam =>
        String(exam.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(exam.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        exam.topics.some(topic => topic.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(exam => exam.category === selectedCategory);
    }
    
    // Filter by difficulty/type
    if (selectedFilter !== 'all') {
      switch (selectedFilter) {
        case 'easy':
          filtered = filtered.filter(exam => exam.difficulty === 'easy');
          break;
        case 'medium':
          filtered = filtered.filter(exam => exam.difficulty === 'medium');
          break;
        case 'hard':
          filtered = filtered.filter(exam => exam.difficulty === 'hard' || exam.difficulty === 'very-hard');
          break;
        case 'timed':
          filtered = filtered.filter(exam => exam.isTimed);
          break;
        case 'untimed':
          filtered = filtered.filter(exam => !exam.isTimed);
          break;
        case 'free':
          filtered = filtered.filter(exam => exam.isFree);
          break;
        case 'recent':
          // Sort by recent (simplified)
          filtered = filtered.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
          break;
      }
    }
    
    setFilteredExams(filtered);
  };

  const toggleBookmark = async (examId) => {
    try {
      let newBookmarks;
      if (bookmarkedExams.includes(examId)) {
        newBookmarks = bookmarkedExams.filter(id => id !== examId);
      } else {
        newBookmarks = [...bookmarkedExams, examId];
      }
      
      setBookmarkedExams(newBookmarks);
      await AsyncStorage.setItem('bookmarkedExams', JSON.stringify(newBookmarks));
      
      // Show feedback
      const isBookmarked = newBookmarks.includes(examId);
      Alert.alert(
        isBookmarked ? 'Bookmarked!' : 'Bookmark Removed',
        isBookmarked 
          ? 'Exam added to your bookmarks' 
          : 'Exam removed from bookmarks'
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const startExam = (exam) => {
    if (!exam.isFree && !isOnline) {
      Alert.alert('Purchase Required', 'This exam requires purchase. Please connect to the internet to purchase.');
      return;
    }
    
    setSelectedExam(exam);
    setExamModalVisible(true);
  };

  const confirmStartExam = () => {
    setExamModalVisible(false);
    setTakingExam(true);
    setCurrentExam(selectedExam);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    
    // Initialize timer if exam is timed
    if (selectedExam.isTimed) {
      setTimeLeft(selectedExam.duration * 60); // Convert minutes to seconds
      startTimer();
    }
  };

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          submitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const submitExam = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Calculate score
    const questions = currentExam.questionsData;
    let correct = 0;
    userAnswers.forEach((answer, index) => {
      if (answer === questions[index].correctAnswer) {
        correct++;
      }
    });
    
    const score = Math.round((correct / questions.length) * 100);
    const passed = score >= currentExam.passingScore;
    
    // Save result
    const result = {
      examId: currentExam.id,
      examTitle: currentExam.title,
      score,
      passed,
      totalQuestions: questions.length,
      correctAnswers: correct,
      timeSpent: currentExam.isTimed ? currentExam.duration * 60 - timeLeft : null,
      date: new Date().toISOString(),
      answers: userAnswers,
    };
    
    // Update completed exams
    const newCompleted = [...completedExams, currentExam.id];
    setCompletedExams(newCompleted);
    await AsyncStorage.setItem('completedExams', JSON.stringify(newCompleted));
    
    // Update results
    const newResults = { ...examResults, [currentExam.id]: result };
    setExamResults(newResults);
    await AsyncStorage.setItem('examResults', JSON.stringify(newResults));
    
    // Show result
    setSelectedResult(result);
    setResultModalVisible(true);
    setTakingExam(false);
  };

  const shareExam = async (exam) => {
    try {
      await Share.share({
        title: exam.title,
        message: `Check out this practice exam: ${exam.title}\n\n${exam.description}\n\nDifficulty: ${exam.difficulty}\nQuestions: ${exam.questions}\nDuration: ${exam.duration} minutes`,
      });
    } catch (error) {
      console.error('Error sharing exam:', error);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'hard': return '#ef4444';
      case 'very-hard': return '#7c2d12';
      default: return '#6b7280';
    }
  };

  const getCategoryGradient = (category) => {
    return gradients[category] || gradients.primary;
  };

  const renderLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color={darkMode ? "#6366f1" : "#6366f1"} />
      <Text style={styles.loadingText}>Loading practice exams...</Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.errorContainer}>
      <MaterialCommunityIcons name="alert-circle-outline" size={64} color="#ef4444" />
      <Text style={styles.errorText}>{error}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={loadInitialData}>
        <Text style={styles.retryButtonText}>Retry</Text>
      </TouchableOpacity>
    </View>
  );

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.backButton} 
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>📝 Practice Exams</Text>
          <TouchableOpacity 
            style={styles.statsButton}
            onPress={() => navigation.navigate('ExamStats')}
          >
            <MaterialCommunityIcons name="chart-box" size={24} color="#ffffff" />
          </TouchableOpacity>
        </View>
        
        <Text style={styles.headerSubtitle}>
          Test your engineering knowledge with timed practice exams and quizzes
        </Text>
      </LinearGradient>
    </Animated.View>
  );

  const renderStats = () => (
    <Animated.View 
      style={[
        styles.statsContainer,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={styles.statsTitle}>📊 Your Exam Performance</Text>
      
      <View style={styles.statsGrid}>
        <View style={styles.statCard}>
          <LinearGradient
            colors={gradients.success}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="clipboard-check" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.completed}</Text>
          <Text style={styles.statLabel}>Completed</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={gradients.blue}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="chart-line" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.averageScore.toFixed(1)}%</Text>
          <Text style={styles.statLabel}>Avg Score</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={gradients.warning}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="clock" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>{stats.totalTimeSpent}</Text>
          <Text style={styles.statLabel}>Minutes</Text>
        </View>
        
        <View style={styles.statCard}>
          <LinearGradient
            colors={gradients.purple}
            style={styles.statGradient}
          >
            <MaterialCommunityIcons name="trophy" size={24} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.statNumber}>
            {Object.values(examResults).filter(r => r.passed).length}
          </Text>
          <Text style={styles.statLabel}>Passed</Text>
        </View>
      </View>
    </Animated.View>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <MaterialCommunityIcons name="magnify" size={24} color={darkMode ? "#a0aec0" : "#6b7280"} />
      <TextInput
        style={styles.searchInput}
        placeholder="Search exams, topics, subjects..."
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons name="close-circle" size={20} color={darkMode ? "#a0aec0" : "#6b7280"} />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderTipsModal = () => {
    const tips = [
      { icon: 'clock-outline', title: 'Manage your time', text: 'Scan all questions first, then budget time per question. Flag hard ones and return to them.' },
      { icon: 'check-circle-outline', title: 'Answer easy questions first', text: 'Bank the marks you know quickly, then spend the remaining time on the tougher items.' },
      { icon: 'book-open-variant', title: 'Read carefully', text: 'Watch for words like "not", "always", and "except" — they change the whole question.' },
      { icon: 'calculator-variant', title: 'Show your working', text: 'For numerical problems, write each step. Partial credit often rewards correct method.' },
      { icon: 'lightbulb-on-outline', title: 'Eliminate wrong options', text: 'On multiple choice, rule out clearly wrong answers to improve your odds on the rest.' },
      { icon: 'sleep', title: 'Rest before the exam', text: 'A clear head beats last-minute cramming. Sleep, hydrate, and arrive early.' },
    ];
    return (
      <Modal
        visible={tipsModalVisible}
        animationType="slide"
        transparent
        onRequestClose={() => setTipsModalVisible(false)}
      >
        <View style={styles.tipsOverlay}>
          <View style={styles.tipsSheet}>
            <View style={styles.tipsHeader}>
              <Text style={styles.tipsHeaderTitle}>💡 Exam Tips</Text>
              <TouchableOpacity onPress={() => setTipsModalVisible(false)} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
                <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#cbd5e0' : '#6b7280'} />
              </TouchableOpacity>
            </View>
            <ScrollView showsVerticalScrollIndicator={false}>
              {tips.map((t, i) => (
                <View key={i} style={styles.tipRow}>
                  <View style={styles.tipIcon}>
                    <MaterialCommunityIcons name={t.icon} size={22} color="#6366f1" />
                  </View>
                  <View style={styles.tipTextWrap}>
                    <Text style={styles.tipTitle}>{t.title}</Text>
                    <Text style={styles.tipText}>{t.text}</Text>
                  </View>
                </View>
              ))}
              <View style={{ height: 16 }} />
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  const renderCategoryFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.categoryScroll}
      contentContainerStyle={styles.categoryContainer}
    >
      {categories.map((category) => (
        <TouchableOpacity
          key={category.id}
          style={[
            styles.categoryChip,
            selectedCategory === category.id && styles.categoryChipActive,
          ]}
          onPress={() => setSelectedCategory(category.id)}
        >
          <MaterialCommunityIcons 
            name={category.icon} 
            size={16} 
            color={selectedCategory === category.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.categoryText,
            selectedCategory === category.id && styles.categoryTextActive,
          ]}>
            {category.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderDifficultyFilter = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false} 
      style={styles.filterScroll}
      contentContainerStyle={styles.filterContainer}
    >
      {filters.map((filter) => (
        <TouchableOpacity
          key={filter.id}
          style={[
            styles.filterChip,
            selectedFilter === filter.id && styles.filterChipActive,
          ]}
          onPress={() => setSelectedFilter(filter.id)}
        >
          <MaterialCommunityIcons 
            name={filter.icon} 
            size={14} 
            color={selectedFilter === filter.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
          />
          <Text style={[
            styles.filterText,
            selectedFilter === filter.id && styles.filterTextActive,
          ]}>
            {filter.label}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderExamCard = (exam) => {
    const isBookmarked = bookmarkedExams.includes(exam.id);
    const isCompleted = completedExams.includes(exam.id);
    const result = examResults[exam.id];
    
    return (
      <TouchableOpacity
        key={exam.id}
        style={styles.examCard}
        onPress={() => startExam(exam)}
      >
        <View style={styles.cardHeader}>
          <LinearGradient
            colors={getCategoryGradient(exam.category)}
            style={styles.examIcon}
          >
            <MaterialCommunityIcons name={exam.icon} size={20} color="#ffffff" />
          </LinearGradient>
          
          <View style={styles.cardHeaderInfo}>
            <Text style={styles.examTitle} numberOfLines={2}>{exam.title}</Text>
            <View style={styles.examMeta}>
              <Text style={styles.examCategory}>{String(exam.category ?? '').toUpperCase()}</Text>
              <View style={[
                styles.difficultyBadge,
                { backgroundColor: getDifficultyColor(exam.difficulty) }
              ]}>
                <Text style={styles.difficultyText}>{String(exam.difficulty ?? '').toUpperCase()}</Text>
              </View>
            </View>
          </View>
          
          <TouchableOpacity 
            onPress={() => toggleBookmark(exam.id)}
            style={styles.bookmarkButton}
          >
            <MaterialCommunityIcons 
              name={isBookmarked ? "bookmark" : "bookmark-outline"} 
              size={20} 
              color={isBookmarked ? "#6366f1" : (darkMode ? "#a0aec0" : "#6b7280")} 
            />
          </TouchableOpacity>
        </View>
        
        <Text style={styles.examDescription} numberOfLines={2}>
          {exam.description}
        </Text>
        
        <View style={styles.examStats}>
          <View style={styles.statItem}>
            <MaterialCommunityIcons name="clock-outline" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.statText}>{exam.duration} min</Text>
          </View>
          <View style={styles.statItem}>
            <MaterialCommunityIcons name="help-circle" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.statText}>{exam.questions} Qs</Text>
          </View>
          <View style={styles.statItem}>
            <MaterialCommunityIcons name="account-group" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.statText}>{exam.attempts.toLocaleString()}</Text>
          </View>
          <View style={styles.statItem}>
            <MaterialCommunityIcons name="chart-line" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={styles.statText}>{exam.averageScore}% avg</Text>
          </View>
        </View>
        
        {isCompleted && result && (
          <View style={styles.resultPreview}>
            <View style={[
              styles.scoreBadge,
              { backgroundColor: result.passed ? '#10b98120' : '#ef444420' }
            ]}>
              <MaterialCommunityIcons 
                name={result.passed ? "trophy" : "alert-circle"} 
                size={16} 
                color={result.passed ? '#10b981' : '#ef4444'} 
              />
              <Text style={[
                styles.scoreText,
                { color: result.passed ? '#10b981' : '#ef4444' }
              ]}>
                Your Score: {result.score}% ({result.passed ? 'PASS' : 'FAIL'})
              </Text>
            </View>
          </View>
        )}
        
        <View style={styles.topicsContainer}>
          {exam.topics.slice(0, 3).map((topic, index) => (
            <View key={index} style={styles.topicTag}>
              <Text style={styles.topicText}>{topic}</Text>
            </View>
          ))}
          {exam.topics.length > 3 && (
            <View style={styles.moreTag}>
              <Text style={styles.moreText}>+{exam.topics.length - 3} more</Text>
            </View>
          )}
        </View>
        
        <View style={styles.cardActions}>
          <TouchableOpacity 
            style={[
              styles.actionButton,
              styles.startButton,
              isCompleted && styles.reviewButton
            ]}
            onPress={() => startExam(exam)}
          >
            <MaterialCommunityIcons 
              name={isCompleted ? "refresh" : "play-circle"} 
              size={16} 
              color="#ffffff" 
            />
            <Text style={styles.actionButtonText}>
              {isCompleted ? 'Retake' : 'Start Exam'}
            </Text>
          </TouchableOpacity>
          
          {isCompleted && (
            <TouchableOpacity 
              style={[styles.actionButton, styles.resultsButton]}
              onPress={() => {
                setSelectedResult(result);
                setResultModalVisible(true);
              }}
            >
              <MaterialCommunityIcons name="chart-box" size={16} color="#6366f1" />
              <Text style={styles.resultsButtonText}>Results</Text>
            </TouchableOpacity>
          )}
          
          <TouchableOpacity 
            style={[styles.actionButton, styles.shareButton]}
            onPress={() => shareExam(exam)}
          >
            <MaterialCommunityIcons name="share-variant" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
          </TouchableOpacity>
        </View>
        
        {!exam.isFree && (
          <View style={styles.premiumBadge}>
            <MaterialCommunityIcons name="crown" size={14} color="#f59e0b" />
            <Text style={styles.premiumText}>PREMIUM - ${exam.price}</Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  const renderExamModal = () => (
    <Modal
      visible={examModalVisible}
      animationType="slide"
      onRequestClose={() => setExamModalVisible(false)}
    >
      {selectedExam && (
        <SafeAreaView style={styles.modalContainer}>
          <LinearGradient
            colors={getCategoryGradient(selectedExam.category)}
            style={styles.modalHeader}
          >
            <View style={styles.modalHeaderContent}>
              <TouchableOpacity 
                style={styles.modalCloseButton}
                onPress={() => setExamModalVisible(false)}
              >
                <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
              </TouchableOpacity>
              <Text style={styles.modalTitle}>{selectedExam.title}</Text>
              <Text style={styles.modalSubtitle}>Exam Instructions</Text>
            </View>
          </LinearGradient>

          <ScrollView style={styles.modalContent}>
            <View style={styles.examInstructions}>
              <Text style={styles.instructionTitle}>📋 Exam Details</Text>
              
              <View style={styles.instructionGrid}>
                <View style={styles.instructionItem}>
                  <MaterialCommunityIcons name="clock" size={20} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.instructionLabel}>Duration</Text>
                  <Text style={styles.instructionValue}>
                    {selectedExam.duration} minutes
                    {selectedExam.isTimed && ' (Timed)'}
                  </Text>
                </View>
                
                <View style={styles.instructionItem}>
                  <MaterialCommunityIcons name="help-circle" size={20} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.instructionLabel}>Questions</Text>
                  <Text style={styles.instructionValue}>{selectedExam.questions}</Text>
                </View>
                
                <View style={styles.instructionItem}>
                  <MaterialCommunityIcons name="target" size={20} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.instructionLabel}>Passing Score</Text>
                  <Text style={styles.instructionValue}>{selectedExam.passingScore}%</Text>
                </View>
                
                <View style={styles.instructionItem}>
                  <MaterialCommunityIcons name="trending-up" size={20} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.instructionLabel}>Difficulty</Text>
                  <Text style={styles.instructionValue}>{selectedExam.difficulty}</Text>
                </View>
              </View>
              
              <Text style={styles.sectionTitle}>Instructions</Text>
              <View style={styles.instructionList}>
                <View style={styles.bulletItem}>
                  <MaterialCommunityIcons name="numeric-1-circle" size={16} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.bulletText}>
                    This exam consists of {selectedExam.questions} multiple-choice questions
                  </Text>
                </View>
                
                <View style={styles.bulletItem}>
                  <MaterialCommunityIcons name="numeric-2-circle" size={16} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.bulletText}>
                    {selectedExam.isTimed 
                      ? `You have ${selectedExam.duration} minutes to complete the exam`
                      : 'This is an untimed exam - take as long as you need'}
                  </Text>
                </View>
                
                <View style={styles.bulletItem}>
                  <MaterialCommunityIcons name="numeric-3-circle" size={16} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.bulletText}>
                    Each question has 4 options - choose the best answer
                  </Text>
                </View>
                
                <View style={styles.bulletItem}>
                  <MaterialCommunityIcons name="numeric-4-circle" size={16} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.bulletText}>
                    You can review your answers before submitting
                  </Text>
                </View>
                
                <View style={styles.bulletItem}>
                  <MaterialCommunityIcons name="numeric-5-circle" size={16} color={getCategoryGradient(selectedExam.category)[0]} />
                  <Text style={styles.bulletText}>
                    Passing score is {selectedExam.passingScore}%
                  </Text>
                </View>
              </View>
              
              <Text style={styles.sectionTitle}>Topics Covered</Text>
              <View style={styles.topicsGrid}>
                {selectedExam.topics.map((topic, index) => (
                  <View key={index} style={styles.modalTopicTag}>
                    <Text style={styles.modalTopicText}>{topic}</Text>
                  </View>
                ))}
              </View>
              
              {!selectedExam.isFree && (
                <View style={styles.premiumNotice}>
                  <MaterialCommunityIcons name="crown" size={24} color="#f59e0b" />
                  <Text style={styles.premiumNoticeText}>
                    This is a premium exam. You will be prompted to purchase before starting.
                  </Text>
                </View>
              )}
            </View>
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity 
              style={styles.cancelButton}
              onPress={() => setExamModalVisible(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.startExamButton}
              onPress={confirmStartExam}
            >
              <LinearGradient
                colors={getCategoryGradient(selectedExam.category)}
                style={styles.startButtonGradient}
              >
                <MaterialCommunityIcons name="play-circle" size={20} color="#ffffff" />
                <Text style={styles.startExamButtonText}>Start Exam</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderExamInterface = () => {
    if (!currentExam || !takingExam) return null;
    
    const currentQuestion = currentExam.questionsData[currentQuestionIndex];
    
    return (
      <Modal
        visible={takingExam}
        animationType="slide"
        onRequestClose={() => {
          Alert.alert(
            'Leave Exam?',
            'Are you sure you want to leave? Your progress will be lost.',
            [
              { text: 'Cancel', style: 'cancel' },
              { 
                text: 'Leave', 
                style: 'destructive',
                onPress: () => {
                  if (timerRef.current) clearInterval(timerRef.current);
                  setTakingExam(false);
                }
              }
            ]
          );
        }}
      >
        <SafeAreaView style={styles.examContainer}>
          {/* Header with timer and progress */}
          <View style={styles.examHeader}>
            <TouchableOpacity 
              style={styles.exitButton}
              onPress={() => {
                Alert.alert(
                  'Leave Exam?',
                  'Are you sure you want to leave? Your progress will be lost.',
                  [
                    { text: 'Cancel', style: 'cancel' },
                    { 
                      text: 'Leave', 
                      style: 'destructive',
                      onPress: () => {
                        if (timerRef.current) clearInterval(timerRef.current);
                        setTakingExam(false);
                      }
                    }
                  ]
                );
              }}
            >
              <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
            </TouchableOpacity>
            
            <View style={styles.examHeaderInfo}>
              <Text style={styles.examHeaderTitle} numberOfLines={1}>
                {currentExam.title}
              </Text>
              
              <View style={styles.examProgress}>
                <Text style={styles.progressText}>
                  Question {currentQuestionIndex + 1} of {currentExam.questionsData.length}
                </Text>
                <View style={styles.progressBar}>
                  <View style={[
                    styles.progressFill,
                    { 
                      width: `${((currentQuestionIndex + 1) / currentExam.questionsData.length) * 100}%`,
                      backgroundColor: getCategoryGradient(currentExam.category)[0]
                    }
                  ]} />
                </View>
              </View>
            </View>
            
            {currentExam.isTimed && (
              <View style={[
                styles.timerContainer,
                timeLeft < 300 && { backgroundColor: '#ef4444' } // Red when less than 5 minutes
              ]}>
                <MaterialCommunityIcons name="timer" size={20} color="#ffffff" />
                <Text style={styles.timerText}>{formatTime(timeLeft)}</Text>
              </View>
            )}
          </View>
          
          {/* Question */}
          <ScrollView style={styles.questionContainer}>
            <View style={styles.questionHeader}>
              <View style={styles.questionMeta}>
                <View style={[
                  styles.difficultyIndicator,
                  { backgroundColor: getDifficultyColor(currentQuestion.difficulty) }
                ]}>
                  <Text style={styles.difficultyIndicatorText}>
                    {String(currentQuestion.difficulty ?? '').toUpperCase()}
                  </Text>
                </View>
                <Text style={styles.topicText}>{currentQuestion.topic}</Text>
              </View>
              
              <Text style={styles.questionNumber}>
                Question {currentQuestionIndex + 1}
              </Text>
            </View>
            
            <Text style={styles.questionText}>{currentQuestion.question}</Text>
            
            {/* Options */}
            <View style={styles.optionsContainer}>
              {currentQuestion.options.map((option, index) => {
                const isSelected = userAnswers[currentQuestionIndex] === index;
                return (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.optionButton,
                      isSelected && styles.optionButtonSelected
                    ]}
                    onPress={() => {
                      const newAnswers = [...userAnswers];
                      newAnswers[currentQuestionIndex] = index;
                      setUserAnswers(newAnswers);
                    }}
                  >
                    <View style={[
                      styles.optionIndicator,
                      isSelected && styles.optionIndicatorSelected
                    ]}>
                      <Text style={[
                        styles.optionLetter,
                        isSelected && styles.optionLetterSelected
                      ]}>
                        {String.fromCharCode(65 + index)}
                      </Text>
                    </View>
                    <Text style={[
                      styles.optionText,
                      isSelected && styles.optionTextSelected
                    ]}>
                      {option}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </ScrollView>
          
          {/* Navigation */}
          <View style={styles.examNavigation}>
            <TouchableOpacity
              style={[
                styles.navButton,
                currentQuestionIndex === 0 && styles.navButtonDisabled
              ]}
              onPress={() => {
                if (currentQuestionIndex > 0) {
                  setCurrentQuestionIndex(currentQuestionIndex - 1);
                }
              }}
              disabled={currentQuestionIndex === 0}
            >
              <MaterialCommunityIcons name="chevron-left" size={20} color="#ffffff" />
              <Text style={styles.navButtonText}>Previous</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.flagButton}
              onPress={() => {
                // Implement flagging feature
                Alert.alert('Question Flagged', 'You can review flagged questions later.');
              }}
            >
              <MaterialCommunityIcons name="flag" size={20} color="#f59e0b" />
            </TouchableOpacity>
            
            {currentQuestionIndex < currentExam.questionsData.length - 1 ? (
              <TouchableOpacity
                style={styles.navButton}
                onPress={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
              >
                <Text style={styles.navButtonText}>Next</Text>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#ffffff" />
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={styles.submitButton}
                onPress={() => {
                  Alert.alert(
                    'Submit Exam?',
                    'Are you sure you want to submit? You cannot change answers after submission.',
                    [
                      { text: 'Cancel', style: 'cancel' },
                      { text: 'Submit', onPress: submitExam }
                    ]
                  );
                }}
              >
                <MaterialCommunityIcons name="send" size={20} color="#ffffff" />
                <Text style={styles.submitButtonText}>Submit Exam</Text>
              </TouchableOpacity>
            )}
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  const renderResultModal = () => (
    <Modal
      visible={resultModalVisible}
      animationType="slide"
      onRequestClose={() => setResultModalVisible(false)}
    >
      {selectedResult && (
        <SafeAreaView style={styles.modalContainer}>
          <LinearGradient
            colors={selectedResult.passed ? gradients.success : gradients.danger}
            style={styles.modalHeader}
          >
            <View style={styles.modalHeaderContent}>
              <TouchableOpacity 
                style={styles.modalCloseButton}
                onPress={() => setResultModalVisible(false)}
              >
                <MaterialCommunityIcons name="close" size={24} color="#ffffff" />
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Exam Results</Text>
              <Text style={styles.modalSubtitle}>{selectedResult.examTitle}</Text>
            </View>
          </LinearGradient>

          <ScrollView style={styles.modalContent}>
            <View style={styles.resultSummary}>
              <View style={[
                styles.resultCircle,
                { borderColor: selectedResult.passed ? '#10b981' : '#ef4444' }
              ]}>
                <Text style={[
                  styles.resultScore,
                  { color: selectedResult.passed ? '#10b981' : '#ef4444' }
                ]}>
                  {selectedResult.score}%
                </Text>
                <Text style={styles.resultLabel}>SCORE</Text>
              </View>
              
              <View style={styles.resultStats}>
                <View style={styles.resultStat}>
                  <Text style={styles.resultStatValue}>{selectedResult.correctAnswers}</Text>
                  <Text style={styles.resultStatLabel}>Correct</Text>
                </View>
                
                <View style={styles.resultStat}>
                  <Text style={styles.resultStatValue}>
                    {selectedResult.totalQuestions - selectedResult.correctAnswers}
                  </Text>
                  <Text style={styles.resultStatLabel}>Incorrect</Text>
                </View>
                
                <View style={styles.resultStat}>
                  <Text style={styles.resultStatValue}>
                    {Math.round((selectedResult.correctAnswers / selectedResult.totalQuestions) * 100)}%
                  </Text>
                  <Text style={styles.resultStatLabel}>Accuracy</Text>
                </View>
              </View>
            </View>
            
            <View style={styles.resultStatus}>
              <MaterialCommunityIcons 
                name={selectedResult.passed ? "trophy" : "alert-circle"} 
                size={32} 
                color={selectedResult.passed ? '#10b981' : '#ef4444'} 
              />
              <Text style={[
                styles.statusText,
                { color: selectedResult.passed ? '#10b981' : '#ef4444' }
              ]}>
                {selectedResult.passed ? '🎉 Congratulations! You Passed!' : '📝 Keep Practicing!'}
              </Text>
            </View>
            
            <Text style={styles.sectionTitle}>Performance Breakdown</Text>
            <View style={styles.performanceChart}>
              <View style={styles.chartBar}>
                <View style={[
                  styles.chartFill,
                  { 
                    width: `${selectedResult.score}%`,
                    backgroundColor: selectedResult.passed ? '#10b981' : '#ef4444'
                  }
                ]} />
                <Text style={styles.chartLabel}>Your Score: {selectedResult.score}%</Text>
              </View>
              
              <View style={styles.chartBar}>
                <View style={[
                  styles.chartFill,
                  { 
                    width: '70%',
                    backgroundColor: '#6b7280'
                  }
                ]} />
                <Text style={styles.chartLabel}>Passing Score: 70%</Text>
              </View>
            </View>
            
            <Text style={styles.sectionTitle}>Recommendations</Text>
            <View style={styles.recommendations}>
              {selectedResult.passed ? (
                <>
                  <View style={styles.recommendationItem}>
                    <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
                    <Text style={styles.recommendationText}>
                      Excellent performance! Consider taking more advanced exams.
                    </Text>
                  </View>
                  <View style={styles.recommendationItem}>
                    <MaterialCommunityIcons name="book-open" size={20} color="#3b82f6" />
                    <Text style={styles.recommendationText}>
                      Review incorrect answers to strengthen weak areas.
                    </Text>
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.recommendationItem}>
                    <MaterialCommunityIcons name="book-open" size={20} color="#3b82f6" />
                    <Text style={styles.recommendationText}>
                      Review the topics where you scored below 60%.
                    </Text>
                  </View>
                  <View style={styles.recommendationItem}>
                    <MaterialCommunityIcons name="clock" size={20} color="#f59e0b" />
                    <Text style={styles.recommendationText}>
                      Practice time management with timed quizzes.
                    </Text>
                  </View>
                  <View style={styles.recommendationItem}>
                    <MaterialCommunityIcons name="refresh" size={20} color="#6366f1" />
                    <Text style={styles.recommendationText}>
                      Retake this exam after reviewing the material.
                    </Text>
                  </View>
                </>
              )}
            </View>
            
            <Text style={styles.sectionTitle}>Detailed Analysis</Text>
            <View style={styles.analysis}>
              <View style={styles.analysisRow}>
                <Text style={styles.analysisLabel}>Date Completed:</Text>
                <Text style={styles.analysisValue}>
                  {new Date(selectedResult.date).toLocaleDateString()}
                </Text>
              </View>
              {selectedResult.timeSpent && (
                <View style={styles.analysisRow}>
                  <Text style={styles.analysisLabel}>Time Spent:</Text>
                  <Text style={styles.analysisValue}>
                    {Math.floor(selectedResult.timeSpent / 60)} minutes {selectedResult.timeSpent % 60} seconds
                  </Text>
                </View>
              )}
              <View style={styles.analysisRow}>
                <Text style={styles.analysisLabel}>Questions Attempted:</Text>
                <Text style={styles.analysisValue}>{selectedResult.totalQuestions}</Text>
              </View>
            </View>
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity 
              style={styles.closeResultButton}
              onPress={() => setResultModalVisible(false)}
            >
              <Text style={styles.closeResultButtonText}>Close</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.reviewButtonModal}
              onPress={() => {
                setResultModalVisible(false);
                // Navigate to review screen with detailed answers
                Alert.alert('Review Answers', 'Detailed answer review coming soon!');
              }}
            >
              <MaterialCommunityIcons name="chart-box" size={20} color="#6366f1" />
              <Text style={styles.reviewButtonText}>Review Answers</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.retakeButton}
              onPress={() => {
                setResultModalVisible(false);
                startExam(exams.find(e => e.id === selectedResult.examId));
              }}
            >
              <LinearGradient
                colors={gradients.primary}
                style={styles.retakeButtonGradient}
              >
                <MaterialCommunityIcons name="refresh" size={20} color="#ffffff" />
                <Text style={styles.retakeButtonText}>Retake Exam</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="clipboard-text" size={64} color={darkMode ? "#4a5568" : "#d1d5db"} />
      <Text style={styles.emptyStateTitle}>No exams found</Text>
      <Text style={styles.emptyStateText}>
        {searchQuery ? 'Try a different search term' : 'Check back for new practice exams'}
      </Text>
      <TouchableOpacity 
        style={styles.resetButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedFilter('all');
          setSelectedCategory('all');
        }}
      >
        <Text style={styles.resetButtonText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const styles = getStyles(darkMode);

  if (loading) {
    return renderLoading();
  }

  if (error && !isRefreshing) {
    return renderError();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#6366f1" />
      
      {renderHeader()}
      {renderStats()}
      {renderSearchBar()}
      {renderCategoryFilter()}
      {renderDifficultyFilter()}
      
      <FlatList
        data={filteredExams}
        renderItem={({ item }) => renderExamCard(item)}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#6366f1']}
            tintColor="#6366f1"
          />
        }
        ListEmptyComponent={renderEmptyState}
        ListFooterComponent={<View style={styles.bottomPadding} />}
      />

      <TouchableOpacity 
        style={styles.fab}
        onPress={() => setTipsModalVisible(true)}
      >
        <LinearGradient
          colors={gradients.success}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="lightbulb-on" size={24} color="#ffffff" />
        </LinearGradient>
      </TouchableOpacity>

      {renderExamModal()}
      {renderExamInterface()}
      {renderResultModal()}
      {renderTipsModal()}
    </SafeAreaView>
  );
};

const getStyles = (darkMode) => StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
    paddingHorizontal: 32,
  },
  errorText: {
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#374151',
    textAlign: 'center',
    marginVertical: 16,
  },
  retryButton: {
    backgroundColor: '#6366f1',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    marginBottom: 16,
  },
  headerGradient: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 30,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    flex: 1,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  statsButton: {
    padding: 8,
  },
  headerSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
    textAlign: 'center',
    lineHeight: 24,
  },
  statsContainer: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginBottom: 16,
    padding: 20,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  statsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statCard: {
    alignItems: 'center',
    flex: 1,
  },
  statGradient: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginHorizontal: 20,
    marginBottom: 12,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 16,
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginLeft: 12,
  },
  categoryScroll: {
    flexGrow: 0,
    marginBottom: 8,
  },
  categoryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 4,
  },
  categoryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 14,
    borderRadius: 20,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  categoryText: {
    marginLeft: 6,
    fontSize: 13,
    color: darkMode ? '#cbd5e0' : '#374151',
    includeFontPadding: false,
  },
  categoryChipActive: {
    backgroundColor: '#6366f1',
    borderColor: '#6366f1',
  },
  categoryTextActive: {
    color: '#ffffff',
    fontWeight: '600',
  },
  filterScroll: {
    flexGrow: 0,
    marginBottom: 16,
  },
  filterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 2,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 12,
    borderRadius: 16,
    marginRight: 8,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  filterChipActive: {
    backgroundColor: '#6366f1',
    borderColor: '#6366f1',
  },
  filterText: {
    marginLeft: 4,
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#374151',
    includeFontPadding: false,
  },
  filterTextActive: {
    color: '#ffffff',
    fontWeight: '600',
  },
  listContainer: {
    paddingHorizontal: 20,
    paddingBottom: 100,
  },
  bottomPadding: {
    height: 100,
  },
  examCard: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    marginBottom: 16,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    position: 'relative',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  examIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  cardHeaderInfo: {
    flex: 1,
  },
  examTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  examMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  examCategory: {
    fontSize: 11,
    color: darkMode ? '#a0aec0' : '#6b7280',
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginRight: 8,
  },
  difficultyBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  difficultyText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  bookmarkButton: {
    padding: 4,
  },
  examDescription: {
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginBottom: 12,
    lineHeight: 20,
  },
  examStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginLeft: 4,
  },
  resultPreview: {
    marginBottom: 12,
  },
  scoreBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  scoreText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  topicsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  topicTag: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  topicText: {
    fontSize: 10,
    color: darkMode ? '#cbd5e0' : '#4b5563',
  },
  moreTag: {
    backgroundColor: '#6366f1',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  moreText: {
    fontSize: 10,
    color: '#ffffff',
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    flex: 1,
    marginRight: 8,
    justifyContent: 'center',
  },
  startButton: {
    backgroundColor: '#6366f1',
  },
  reviewButton: {
    backgroundColor: '#3b82f6',
  },
  actionButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  resultsButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#6366f1',
    flex: 0.8,
  },
  resultsButtonText: {
    color: '#6366f1',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  shareButton: {
    backgroundColor: 'transparent',
    padding: 8,
    flex: 0,
  },
  premiumBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  premiumText: {
    fontSize: 10,
    color: '#f59e0b',
    fontWeight: 'bold',
    marginLeft: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderRadius: 16,
    marginTop: 20,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    textAlign: 'center',
    marginBottom: 20,
  },
  resetButton: {
    backgroundColor: '#6366f1',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  resetButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  fabGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tipsOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  tipsSheet: {
    backgroundColor: darkMode ? '#1e293b' : '#ffffff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 24,
    maxHeight: '80%',
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  tipsHeaderTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: darkMode ? '#f1f5f9' : '#1a202c',
  },
  tipRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#2d3748' : '#f1f5f9',
  },
  tipIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: darkMode ? '#312e81' : '#eef2ff',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  tipTextWrap: {
    flex: 1,
  },
  tipTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: darkMode ? '#f1f5f9' : '#1a202c',
    marginBottom: 2,
  },
  tipText: {
    fontSize: 13,
    lineHeight: 19,
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  modalHeader: {
    paddingTop: 20,
    paddingBottom: 30,
  },
  modalHeaderContent: {
    paddingHorizontal: 20,
  },
  modalCloseButton: {
    padding: 8,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.9,
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  examInstructions: {
    marginBottom: 32,
  },
  instructionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 16,
  },
  instructionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  instructionItem: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  instructionLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginTop: 8,
    marginBottom: 4,
  },
  instructionValue: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginTop: 24,
    marginBottom: 12,
  },
  instructionList: {
    marginBottom: 20,
  },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 8,
    lineHeight: 20,
  },
  topicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  modalTopicTag: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
  },
  modalTopicText: {
    fontSize: 12,
    color: darkMode ? '#cbd5e0' : '#4b5563',
  },
  premiumNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    padding: 16,
    borderRadius: 12,
    marginTop: 20,
  },
  premiumNoticeText: {
    flex: 1,
    fontSize: 14,
    color: '#f59e0b',
    marginLeft: 12,
    fontWeight: '500',
  },
  modalActions: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginRight: 12,
  },
  cancelButtonText: {
    color: darkMode ? '#a0aec0' : '#6b7280',
    fontSize: 16,
    fontWeight: '600',
  },
  startExamButton: {
    flex: 1,
  },
  startButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
  },
  startExamButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  examContainer: {
    flex: 1,
    backgroundColor: darkMode ? '#1a202c' : '#f0f4f8',
  },
  examHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  exitButton: {
    padding: 8,
  },
  examHeaderInfo: {
    flex: 1,
    marginHorizontal: 12,
  },
  examHeaderTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  examProgress: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  progressText: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginRight: 12,
  },
  progressBar: {
    flex: 1,
    height: 4,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  timerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  timerText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 4,
  },
  questionContainer: {
    flex: 1,
    padding: 20,
  },
  questionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  questionMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  difficultyIndicator: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginRight: 8,
  },
  difficultyIndicatorText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  questionNumber: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
    fontWeight: '600',
  },
  questionText: {
    fontSize: 18,
    color: darkMode ? '#f7fafc' : '#1f2937',
    lineHeight: 28,
    marginBottom: 30,
  },
  optionsContainer: {
    marginBottom: 20,
  },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  optionButtonSelected: {
    borderColor: '#6366f1',
    backgroundColor: darkMode ? '#2d3748' : '#f0f4ff',
  },
  optionIndicator: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  optionIndicatorSelected: {
    backgroundColor: '#6366f1',
  },
  optionLetter: {
    fontSize: 14,
    fontWeight: 'bold',
    color: darkMode ? '#cbd5e0' : '#6b7280',
  },
  optionLetterSelected: {
    color: '#ffffff',
  },
  optionText: {
    flex: 1,
    fontSize: 16,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    lineHeight: 24,
  },
  optionTextSelected: {
    color: darkMode ? '#f7fafc' : '#1f2937',
    fontWeight: '500',
  },
  examNavigation: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    borderTopWidth: 1,
    borderTopColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  navButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#6366f1',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
  },
  navButtonDisabled: {
    backgroundColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginHorizontal: 8,
  },
  flagButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  resultSummary: {
    alignItems: 'center',
    marginBottom: 24,
  },
  resultCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 6,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  resultScore: {
    fontSize: 36,
    fontWeight: 'bold',
  },
  resultLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
    marginTop: 4,
  },
  resultStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  resultStat: {
    alignItems: 'center',
    flex: 1,
  },
  resultStatValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: darkMode ? '#f7fafc' : '#1f2937',
    marginBottom: 4,
  },
  resultStatLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  resultStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
  },
  statusText: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 12,
  },
  performanceChart: {
    marginBottom: 24,
  },
  chartBar: {
    marginBottom: 12,
  },
  chartFill: {
    height: 20,
    borderRadius: 10,
    marginBottom: 4,
  },
  chartLabel: {
    fontSize: 12,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  recommendations: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  recommendationText: {
    flex: 1,
    fontSize: 14,
    color: darkMode ? '#cbd5e0' : '#4b5563',
    marginLeft: 8,
    lineHeight: 20,
  },
  analysis: {
    backgroundColor: darkMode ? '#2d3748' : '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
  },
  analysisRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  analysisLabel: {
    fontSize: 14,
    color: darkMode ? '#a0aec0' : '#6b7280',
  },
  analysisValue: {
    fontSize: 14,
    fontWeight: '600',
    color: darkMode ? '#f7fafc' : '#1f2937',
  },
  closeResultButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginRight: 12,
  },
  closeResultButtonText: {
    color: darkMode ? '#a0aec0' : '#6b7280',
    fontSize: 16,
    fontWeight: '600',
  },
  reviewButtonModal: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#6366f1',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginRight: 12,
  },
  reviewButtonText: {
    color: '#6366f1',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  retakeButton: {
    flex: 1,
  },
  retakeButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
  },
  retakeButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
});

export default PracticeExams;